import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    getPrograms,
    createProgram,
    updateProgram,
    deleteProgram
} from "../services/crmService";
import "./ProgramsManagerPage.css";

const emptyForm = {
    name: "",
    subtitle: "",
    description: "",
    badge: "",
    category: "",
    audience: "",
    level: "",
    imageUrl: "",
    metaText: "",
    sortOrder: 0,
    isPublished: true
};

function mapProgramToForm(program) {
    return {
        name: program.name || "",
        subtitle: program.subtitle || "",
        description: program.description || "",
        badge: program.badge || "",
        category: program.category || "",
        audience: program.audience || "",
        level: program.level || "",
        imageUrl: program.imageUrl || "",
        metaText: Array.isArray(program.meta) ? program.meta.join(", ") : "",
        sortOrder: program.sortOrder ?? 0,
        isPublished: !!program.isPublished
    };
}

function buildPayload(form) {
    return {
        name: form.name.trim(),
        subtitle: form.subtitle.trim() || null,
        description: form.description.trim() || null,
        badge: form.badge.trim() || null,
        category: form.category.trim() || null,
        audience: form.audience.trim() || null,
        level: form.level.trim() || null,
        imageUrl: form.imageUrl.trim() || null,
        meta: form.metaText
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean),
        sortOrder: Number(form.sortOrder) || 0,
        isPublished: !!form.isPublished
    };
}

export default function ProgramsManagerPage() {
    const [projectId, setProjectId] = useState("");
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedId, setSelectedId] = useState("");
    const [form, setForm] = useState(emptyForm);
    const [search, setSearch] = useState("");

    const filteredPrograms = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return programs;

        return programs.filter((p) =>
            [
                p.name,
                p.subtitle,
                p.description,
                p.level,
                p.category,
                p.audience
            ]
                .filter(Boolean)
                .some((x) => x.toLowerCase().includes(q))
        );
    }, [programs, search]);

    const loadPrograms = async (id = projectId) => {
        if (!id) {
            toast.error("Enter Project ID first");
            return;
        }

        try {
            setLoading(true);
            const data = await getPrograms(id);
            setPrograms(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load programs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedId) {
            setForm(emptyForm);
            return;
        }

        const selected = programs.find((x) => x.id === selectedId);
        if (selected) {
            setForm(mapProgramToForm(selected));
        }
    }, [selectedId, programs]);

    const updateField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateNew = () => {
        setSelectedId("");
        setForm(emptyForm);
    };

    const handleEdit = (program) => {
        setSelectedId(program.id);
        setForm(mapProgramToForm(program));
    };

    const handleSave = async () => {
        if (!projectId) {
            toast.error("Enter Project ID first");
            return;
        }

        if (!form.name.trim()) {
            toast.error("Program name is required");
            return;
        }

        try {
            setSaving(true);

            const payload = buildPayload(form);

            if (selectedId) {
                const updated = await updateProgram(projectId, selectedId, payload);
                setPrograms((prev) =>
                    prev.map((x) => (x.id === selectedId ? updated : x))
                );
                toast.success("Program updated");
            } else {
                const created = await createProgram(projectId, payload);
                setPrograms((prev) =>
                    [...prev, created].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                );
                setSelectedId(created.id);
                toast.success("Program created");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to save program");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!projectId || !selectedId) return;

        const ok = window.confirm("Delete this program?");
        if (!ok) return;

        try {
            setSaving(true);
            await deleteProgram(projectId, selectedId);
            setPrograms((prev) => prev.filter((x) => x.id !== selectedId));
            setSelectedId("");
            setForm(emptyForm);
            toast.success("Program deleted");
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete program");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="crm-page">
            <div className="crm-shell">
                <div className="crm-header-card">
                    <div>
                        <div className="crm-badge">Shriansh Nexus CRM</div>
                        <h1 className="crm-title">Programs Manager</h1>
                        <p className="crm-subtitle">
                            Manage academy programs generated by AI, then edit them for the live website.
                        </p>
                    </div>
                </div>

                <div className="crm-toolbar">
                    <div className="crm-project-box">
                        <label className="crm-label">Project ID</label>
                        <div className="crm-inline">
                            <input
                                className="crm-input"
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                placeholder="Enter project ID"
                            />
                            <button className="crm-btn crm-btn-primary" onClick={() => loadPrograms()}>
                                {loading ? "Loading..." : "Load"}
                            </button>
                        </div>
                    </div>

                    <div className="crm-search-box">
                        <label className="crm-label">Search</label>
                        <input
                            className="crm-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search programs"
                        />
                    </div>
                </div>

                <div className="crm-grid">
                    <div className="crm-list-card">
                        <div className="crm-list-head">
                            <h2>Programs</h2>
                            <button className="crm-btn crm-btn-secondary" onClick={handleCreateNew}>
                                New Program
                            </button>
                        </div>

                        <div className="crm-list">
                            {filteredPrograms.length === 0 && (
                                <div className="crm-empty">No programs found.</div>
                            )}

                            {filteredPrograms.map((program) => (
                                <button
                                    key={program.id}
                                    className={`crm-list-item ${selectedId === program.id ? "active" : ""}`}
                                    onClick={() => handleEdit(program)}
                                >
                                    <div className="crm-list-item-top">
                                        <strong>{program.name}</strong>
                                        <span className={`crm-status ${program.isPublished ? "published" : "draft"}`}>
                                            {program.isPublished ? "Published" : "Draft"}
                                        </span>
                                    </div>

                                    <div className="crm-list-item-meta">
                                        <span>{program.level || "No level"}</span>
                                        <span>Sort: {program.sortOrder ?? 0}</span>
                                        <span>{program.isAiGenerated ? "AI" : "Manual"}</span>
                                    </div>

                                    {program.description && (
                                        <p className="crm-list-item-desc">{program.description}</p>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="crm-editor-card">
                        <div className="crm-editor-head">
                            <h2>{selectedId ? "Edit Program" : "Create Program"}</h2>
                            <div className="crm-editor-actions">
                                {selectedId && (
                                    <button className="crm-btn crm-btn-danger" onClick={handleDelete} disabled={saving}>
                                        Delete
                                    </button>
                                )}
                                <button className="crm-btn crm-btn-primary" onClick={handleSave} disabled={saving}>
                                    {saving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>

                        <div className="crm-form-grid">
                            <div className="crm-field">
                                <label className="crm-label">Program Name</label>
                                <input
                                    className="crm-input"
                                    value={form.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    placeholder="Football Foundation Program"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Subtitle</label>
                                <input
                                    className="crm-input"
                                    value={form.subtitle}
                                    onChange={(e) => updateField("subtitle", e.target.value)}
                                    placeholder="U-12 Weekend Batch"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Badge</label>
                                <input
                                    className="crm-input"
                                    value={form.badge}
                                    onChange={(e) => updateField("badge", e.target.value)}
                                    placeholder="Popular"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Level</label>
                                <input
                                    className="crm-input"
                                    value={form.level}
                                    onChange={(e) => updateField("level", e.target.value)}
                                    placeholder="Beginner"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Category</label>
                                <input
                                    className="crm-input"
                                    value={form.category}
                                    onChange={(e) => updateField("category", e.target.value)}
                                    placeholder="Football"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Audience</label>
                                <input
                                    className="crm-input"
                                    value={form.audience}
                                    onChange={(e) => updateField("audience", e.target.value)}
                                    placeholder="Kids / Teens / Adults"
                                />
                            </div>

                            <div className="crm-field crm-field-full">
                                <label className="crm-label">Description</label>
                                <textarea
                                    className="crm-textarea"
                                    value={form.description}
                                    onChange={(e) => updateField("description", e.target.value)}
                                    placeholder="Describe the program"
                                />
                            </div>

                            <div className="crm-field crm-field-full">
                                <label className="crm-label">Image URL</label>
                                <input
                                    className="crm-input"
                                    value={form.imageUrl}
                                    onChange={(e) => updateField("imageUrl", e.target.value)}
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="crm-field crm-field-full">
                                <label className="crm-label">Meta Items</label>
                                <input
                                    className="crm-input"
                                    value={form.metaText}
                                    onChange={(e) => updateField("metaText", e.target.value)}
                                    placeholder="Age Group, Weekend Batch, Coach Guided"
                                />
                            </div>

                            <div className="crm-field">
                                <label className="crm-label">Sort Order</label>
                                <input
                                    type="number"
                                    className="crm-input"
                                    value={form.sortOrder}
                                    onChange={(e) => updateField("sortOrder", e.target.value)}
                                />
                            </div>

                            <div className="crm-field crm-toggle-field">
                                <label className="crm-label">Published</label>
                                <label className="crm-toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.isPublished}
                                        onChange={(e) => updateField("isPublished", e.target.checked)}
                                    />
                                    <span>{form.isPublished ? "Yes" : "No"}</span>
                                </label>
                            </div>
                        </div>

                        <div className="crm-preview-card">
                            <div className="crm-preview-badge">Live Card Preview</div>
                            <div className="crm-program-preview">
                                <div className="crm-program-preview-badge">
                                    {form.level || form.badge || "Program"}
                                </div>
                                <h3>{form.name || "Program Name"}</h3>
                                <div className="crm-program-preview-subtitle">
                                    {form.subtitle || "Program subtitle"}
                                </div>
                                <p>{form.description || "Program description preview"}</p>

                                <div className="crm-chip-row">
                                    {form.metaText
                                        .split(",")
                                        .map((x) => x.trim())
                                        .filter(Boolean)
                                        .slice(0, 3)
                                        .map((item) => (
                                            <span className="crm-chip" key={item}>
                                                {item}
                                            </span>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}